import { RouteProp } from '@react-navigation/native';
import React from 'react';
import { ProfilesTabParamList, useGoBack } from '../../../../../utils/appNavigationNames';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import { getIntl } from '../../../../../utils/intl';
import usePersistVisitToClientProfiles from '../../../../../hooks/api/usePersistVisitToClientProfiles';
import VisitForm from '../../../../shared/ResourceForms/VisitForm/Form';
import RenderWithProviders, { ProviderSupportList } from '../../../../shared/ResourceForms/RenderWithProviders';
import { APITypes, MedicalDataTypes } from '../../../../../primary-record-types';

const intl = getIntl('copy-organization-notes-screen', {
    resourceNotes: 'Resource Notes',
    cancel: 'Cancel',
    share: 'Share Visit',
});

export type CopyVisitToClientsScreenOwnProps = {};

export type CopyVisitToClientsScreenProps = CopyVisitToClientsScreenOwnProps & {
    route: RouteProp<ProfilesTabParamList, 'copy-visit-to-clients'>;
};

const CopyVisitToClientsScreen: React.FC<CopyVisitToClientsScreenProps> = ({ route }) => {
    // A single selected client has a concrete care team to offer as the Provider option.
    if (route.params.medicalProfileIds.length === 1) {
        return (
            <RenderWithProviders
                medicalProfileId={route.params.medicalProfileIds[0]}
                includeSupportCareTeamMembers
                render={(providers) => <CopyVisitToClientsForm route={route} providers={providers} />}
            />
        );
    }
    return <CopyVisitToClientsForm route={route} providers={[]} />;
};

const CopyVisitToClientsForm: React.FC<CopyVisitToClientsScreenProps & { providers: ProviderSupportList }> = ({
    route,
    providers,
}) => {
    const goBack = useGoBack();
    const { isPersisting, persistVisitToClientProfiles } = usePersistVisitToClientProfiles({
        onPersistSuccess: goBack,
    });
    return (
        <AnimateLoadedContent isLoading={isPersisting}>
            {() => (
                <VisitForm
                    providers={providers}
                    isDisabled={isPersisting}
                    isLoading={isPersisting}
                    showOrganizationUserSelect
                    autoOpenWhenPicker
                    onSubmit={(data) => {
                        // A recurrence rule expands server-side, one series per client.
                        persistVisitToClientProfiles({
                            resource: data.resourceUpdate as Omit<MedicalDataTypes.PrimaryRecordVisit, 'id'>,
                            images: data.imageUpdates.filter(
                                (_) => _.type === 'create',
                            ) as APITypes.medicalDataService.ResourceImageCreate[],
                            destinationMedicalProfileIds: route.params.medicalProfileIds,
                            sourceNoteId: data.sourceNoteId,
                        });
                    }}
                    profileId="" // No profileId
                    onCancel={goBack}
                    saveText={intl('share')}
                />
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(CopyVisitToClientsScreen);
